import { IconCheck } from "@tabler/icons-react";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";

import classes from "./RadioButton.module.css";

type RadioButtonSize = "standard" | "check";

type VariantStyle = {
  bg: string;
  border: string;
  borderWidth: number;
  hoverBorder: string;
};

const VARIANT_STYLES: Record<
  RadioButtonSize,
  Record<"off" | "on" | "offDisabled" | "onDisabled", VariantStyle>
> = {
  standard: {
    off: {
      bg: colors.bg.elevation.primary,
      border: colors.border.secondaryDefault,
      borderWidth: 1,
      hoverBorder: colors.border.secondaryHover,
    },
    on: {
      bg: "transparent",
      border: colors.border.primaryDefault,
      borderWidth: 7,
      hoverBorder: colors.border.primaryHover,
    },
    offDisabled: {
      bg: colors.bg.button.custom.disabled,
      border: colors.border.secondaryDisabled,
      borderWidth: 1,
      hoverBorder: colors.border.secondaryDisabled,
    },
    onDisabled: {
      bg: "transparent",
      border: colors.border.primaryDisabled,
      borderWidth: 7,
      hoverBorder: colors.border.primaryDisabled,
    },
  },
  check: {
    off: {
      bg: colors.bg.elevation.primary,
      border: colors.border.secondaryDefault,
      borderWidth: 1,
      hoverBorder: colors.border.secondaryHover,
    },
    on: {
      bg: colors.bg.button.primary.default,
      border: "transparent",
      borderWidth: 0,
      hoverBorder: colors.bg.button.primary.hover,
    },
    offDisabled: {
      bg: colors.bg.elevation.tertiary,
      border: colors.border.secondaryDisabled,
      borderWidth: 1,
      hoverBorder: colors.border.secondaryDisabled,
    },
    onDisabled: {
      bg: colors.bg.button.primary.disabled,
      border: "transparent",
      borderWidth: 0,
      hoverBorder: colors.bg.button.primary.disabled,
    },
  },
};

const getVariantStyle = (
  size: RadioButtonSize,
  selected: boolean,
  disabled: boolean,
): VariantStyle => {
  const key = disabled ? (selected ? "onDisabled" : "offDisabled") : selected ? "on" : "off";
  return VARIANT_STYLES[size][key];
};

type RadioButtonProps = {
  selected?: boolean;
  onChange?: (selected: boolean) => void;
  disabled?: boolean;
  size?: RadioButtonSize;
  "aria-label"?: string;
};

const RadioButton = ({
  selected = false,
  onChange,
  disabled = false,
  size = "standard",
  "aria-label": ariaLabel,
}: RadioButtonProps) => {
  const { bg, border, borderWidth, hoverBorder } = getVariantStyle(size, selected, disabled);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      aria-label={ariaLabel}
      className={classes.root}
      onClick={!disabled ? () => onChange?.(!selected) : undefined}
      style={
        {
          "--radio-bg": bg,
          "--radio-border": border,
          "--radio-border-width": `${borderWidth}px`,
          "--radio-hover-border": hoverBorder,
          "--radio-root-size": spacing[6],
          "--radio-root-padding": spacing[0.5],
        } as React.CSSProperties
      }
    >
      <div className={classes.inner}>
        {size === "check" && selected && <IconCheck size={16} stroke={2.5} color="white" />}
      </div>
    </button>
  );
};

export { RadioButton };
